import { Download, Globe, Linkedin, Mail, MapPin } from 'lucide-react'
import React from 'react'
import { SEOHead } from '../components/seo/SEOHead'
import { Button, Card, Container, Heading, Link, Text } from '../components/ui'

/**
 * Resume page component providing comprehensive professional profile
 * optimized for both human readers and search engines
 *
 * Features:
 * - Clean, printable HTML format for accessibility and SEO
 * - PDF download functionality for convenience
 * - Structured sections with semantic HTML
 * - Achievement-focused content with quantifiable results
 * - Responsive layout optimized for all devices
 * - Professional keywords for search optimization
 * - Call-to-action elements for hiring managers
 */
const ResumePage: React.FC = () => {
  const handleDownloadPDF = () => {
    // Download the actual PDF resume from public folder
    const link = document.createElement('a')
    link.href =
      '/Lucas Stufflebeam-Principal Software Architect-Resume 2025.pdf'
    link.download =
      'Lucas Stufflebeam-Principal Software Architect-Resume 2025.pdf'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      {/* SEO Meta Tags for Resume Page */}
      <SEOHead page="resume" path="/resume" />

      <main className="page-main">
        <Container size="lg" padding="lg">
          {/* Resume Header with Actions */}
          <div className="resume-header">
            <div className="resume-title-section">
              <Heading as="h1" size="3xl" variant="default">
                Resume
              </Heading>
              <Text size="lg" variant="secondary">
                Principal Software Architect - Lucas Stufflebeam
              </Text>
            </div>

            <div className="resume-actions no-print">
              <Button
                variant="primary"
                size="md"
                onClick={handleDownloadPDF}
                className="download-button"
              >
                <Download size={16} />
                Download PDF
              </Button>
            </div>
          </div>

          {/* Resume Content */}
          <div className="resume-content">
            {/* Contact Information */}
            <Card variant="surface" padding="lg" className="contact-section">
              <div className="contact-header">
                <div className="contact-identity">
                  <Heading as="h2" size="2xl" variant="gradient">
                    Lucas Stufflebeam
                  </Heading>
                  <Text size="lg" variant="accent" className="contact-title">
                    Principal Software Architect
                  </Text>
                </div>
              </div>

              <div className="contact-details">
                <div className="contact-item">
                  <Globe size={16} />
                  <Link
                    href="https://lucasstufflebeam.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="default"
                  >
                    lucasstufflebeam.com
                  </Link>
                </div>
                <div className="contact-item">
                  <Mail size={16} />
                  <Link
                    href="mailto:lucas@lucasstufflebeam.com"
                    variant="default"
                  >
                    lucas@lucasstufflebeam.com
                  </Link>
                </div>
                <div className="contact-item">
                  <MapPin size={16} />
                  <Text variant="primary">Denver, CO, USA</Text>
                </div>
                <div className="contact-item">
                  <Linkedin size={16} />
                  <Link
                    href="https://linkedin.com/in/lstufflebeam"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="default"
                  >
                    linkedin.com/in/lstufflebeam
                  </Link>
                </div>
              </div>
            </Card>

            {/* Professional Summary */}
            <section className="resume-section">
              <Heading
                as="h3"
                size="xl"
                variant="default"
                className="section-title"
              >
                Summary
              </Heading>
              <Card variant="glass" padding="lg">
                <Text size="base" variant="primary" className="summary-text">
                  Principal Software Architect with 15+ years of experience
                  designing highly concurrent, event-driven systems for
                  global-scale applications. Proven track record in modernizing
                  legacy platforms, reducing cloud costs, and scaling
                  infrastructure. Expert in Kubernetes, Istio, PostgreSQL,
                  MongoDB, Docker, Azure, and LLM DevOps.
                </Text>
              </Card>
            </section>

            {/* Professional Experience */}
            <section className="resume-section">
              <Heading
                as="h3"
                size="xl"
                variant="default"
                className="section-title"
              >
                Professional Experience
              </Heading>

              {/* Principal Software Architect */}
              <Card variant="surface" padding="lg" className="experience-item">
                <div className="experience-header">
                  <div className="experience-title-group">
                    <Heading as="h4" size="lg" variant="default">
                      Principal Software Architect
                    </Heading>
                    <Text size="base" variant="accent" className="company-name">
                      The Regis Company • Golden, CO • Remote
                    </Text>
                  </div>
                  <Text
                    size="sm"
                    variant="tertiary"
                    className="experience-dates"
                  >
                    Aug 2016 - Present
                  </Text>
                </div>

                <ul className="experience-achievements">
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Architected and scaled</strong> a global,
                      high-availability backend platform supporting 100,000+
                      concurrent users across 7 live products
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Led integration of LLM-powered DevOps</strong>{' '}
                      workflows, boosting release reliability and accelerating
                      development cycles
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Enforced security and privacy compliance</strong>{' '}
                      with ISO 27001, ISO 42001, SOC II, GDPR, and other global
                      standards
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Designed cost-optimization strategies</strong>{' '}
                      that maintained infrastructure spend over 5+ years of
                      sustained growth
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Migrated legacy systems</strong> to containerized,
                      cloud-native services using Istio and Kubernetes—achieving
                      zero downtime
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Established engineering standards</strong> for
                      testability, code quality, and observability across 3
                      distinct development teams
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Mentored engineers</strong> and guided
                      cross-functional architecture decisions alongside
                      long-term technical strategy
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Led high-severity incident response</strong>,
                      serving as primary technical liaison with clients and
                      executive leadership
                    </Text>
                  </li>
                </ul>
              </Card>

              {/* Lead Software Engineer - Eclosion Consulting */}
              <Card variant="surface" padding="lg" className="experience-item">
                <div className="experience-header">
                  <div className="experience-title-group">
                    <Heading as="h4" size="lg" variant="default">
                      Lead Software Engineer
                    </Heading>
                    <Text size="base" variant="accent" className="company-name">
                      Eclosion Consulting • Denver, CO • Remote
                    </Text>
                  </div>
                  <Text
                    size="sm"
                    variant="tertiary"
                    className="experience-dates"
                  >
                    Aug 2014 - Present
                  </Text>
                </div>

                <ul className="experience-achievements">
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>
                        Built and maintained custom software systems
                      </strong>{' '}
                      tailored to client business and operational needs
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Deployed applications</strong> in low-connectivity
                      environments, with offline-first design and field
                      resilience
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Advised early-stage clients</strong> on technology
                      strategy, developer hiring, and scalable system
                      architecture
                    </Text>
                  </li>
                </ul>
              </Card>

              {/* Lead Network Engineer */}
              <Card variant="surface" padding="lg" className="experience-item">
                <div className="experience-header">
                  <div className="experience-title-group">
                    <Heading as="h4" size="lg" variant="default">
                      Lead Network Engineer
                    </Heading>
                    <Text size="base" variant="accent" className="company-name">
                      Idol Minds • Denver Metropolitan Area
                    </Text>
                  </div>
                  <Text
                    size="sm"
                    variant="tertiary"
                    className="experience-dates"
                  >
                    Jan 2014 - Aug 2016
                  </Text>
                </div>

                <ul className="experience-achievements">
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Designed and maintained</strong> scalable
                      real-time backend services supporting 10,000+ concurrent
                      players in a shared world
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Developed horizontal scaling</strong> and zone
                      migration system to minimize server cost while maximizing
                      concurrency
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Collaborated with mobile teams</strong> to
                      mitigate network instability across Android, iOS, and
                      console platforms
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Supported cross-platform development</strong> in
                      Unity for Android, iOS, PS4, PC, and Xbox One
                    </Text>
                  </li>
                </ul>
              </Card>

              {/* Senior Software Developer - Amadeus Consulting */}
              <Card variant="surface" padding="lg" className="experience-item">
                <div className="experience-header">
                  <div className="experience-title-group">
                    <Heading as="h4" size="lg" variant="default">
                      Senior Software Developer
                    </Heading>
                    <Text size="base" variant="accent" className="company-name">
                      Amadeus Consulting • Boulder, CO
                    </Text>
                  </div>
                  <Text
                    size="sm"
                    variant="tertiary"
                    className="experience-dates"
                  >
                    Mar 2012 - Aug 2014
                  </Text>
                </div>

                <ul className="experience-achievements">
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Delivered full-stack software solutions</strong>{' '}
                      spanning admin portals, e-commerce, and mobile payments
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Built data mining pipelines</strong> to process
                      large-scale datasets and surface critical business
                      insights
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Improved crawler and DB performance</strong> by
                      700%+ through algorithmic and indexing optimization
                    </Text>
                  </li>
                </ul>
              </Card>

              {/* Senior Mobile Engineer */}
              <Card variant="surface" padding="lg" className="experience-item">
                <div className="experience-header">
                  <div className="experience-title-group">
                    <Heading as="h4" size="lg" variant="default">
                      Senior Mobile Engineer
                    </Heading>
                    <Text size="base" variant="accent" className="company-name">
                      Aeria Games & Entertainment • Santa Clara, CA
                    </Text>
                  </div>
                  <Text
                    size="sm"
                    variant="tertiary"
                    className="experience-dates"
                  >
                    Aug 2012 - Jan 2014
                  </Text>
                </div>

                <ul className="experience-achievements">
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Maintained backend services</strong> and data
                      infrastructure for mobile games with 10,000+ daily active
                      users
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Built rapid prototypes</strong> and gameplay
                      features in Unreal Engine 4 and Unity
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Developed admin tooling</strong> and KPI
                      dashboards for live ops and event-driven game management
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Supported design and execution</strong> of large
                      community-driven competitive events
                    </Text>
                  </li>
                </ul>
              </Card>

              {/* Software Engineer - Artemis Vision */}
              <Card variant="surface" padding="lg" className="experience-item">
                <div className="experience-header">
                  <div className="experience-title-group">
                    <Heading as="h4" size="lg" variant="default">
                      Software Engineer
                    </Heading>
                    <Text size="base" variant="accent" className="company-name">
                      Artemis Vision • Denver, CO
                    </Text>
                  </div>
                  <Text
                    size="sm"
                    variant="tertiary"
                    className="experience-dates"
                  >
                    Jun 2011 - Mar 2012
                  </Text>
                </div>

                <ul className="experience-achievements">
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Developed machine vision systems</strong> to
                      detect product defects in manufacturing using ML and 3D
                      imaging
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Supported robotic automation</strong> by
                      integrating adaptive object recognition and rule-based
                      learning
                    </Text>
                  </li>
                </ul>
              </Card>

              {/* Gameplay Programmer */}
              <Card variant="surface" padding="lg" className="experience-item">
                <div className="experience-header">
                  <div className="experience-title-group">
                    <Heading as="h4" size="lg" variant="default">
                      Gameplay Programmer
                    </Heading>
                    <Text size="base" variant="accent" className="company-name">
                      Black Lantern Studios • Springfield, MO
                    </Text>
                  </div>
                  <Text
                    size="sm"
                    variant="tertiary"
                    className="experience-dates"
                  >
                    Jun 2009 - Jun 2011
                  </Text>
                </div>

                <ul className="experience-achievements">
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Shipped cross-platform titles</strong> for DS,
                      3DS, Wii, PS3, and Xbox 360
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Led end-to-end development</strong> on multiple
                      handheld and console projects
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Added platform support</strong> for PS3, LeapPad,
                      and Leapster within proprietary game engine
                    </Text>
                  </li>
                  <li>
                    <Text size="sm" variant="secondary">
                      <strong>Optimized asset and save data systems</strong> to
                      cut cartridge manufacturing costs by 70%
                    </Text>
                  </li>
                </ul>
              </Card>
            </section>

            {/* Technical Masteries */}
            <section className="resume-section">
              <Heading
                as="h3"
                size="xl"
                variant="default"
                className="section-title"
              >
                Technical Masteries
              </Heading>

              <div className="masteries-grid">
                <Card variant="glass" padding="md" className="mastery-category">
                  <Heading
                    as="h4"
                    size="lg"
                    variant="default"
                    className="mastery-title"
                  >
                    Architecture
                  </Heading>
                  <ul className="mastery-list">
                    <li>
                      <Text size="xs" variant="secondary">
                        Microservices, SOA
                      </Text>
                    </li>
                    <li>
                      <Text size="xs" variant="secondary">
                        Real-time & multiplayer systems
                      </Text>
                    </li>
                    <li>
                      <Text size="xs" variant="secondary">
                        Security & compliance
                      </Text>
                    </li>
                    <li>
                      <Text size="xs" variant="secondary">
                        High-concurrency distributed systems
                      </Text>
                    </li>
                    <li>
                      <Text size="xs" variant="secondary">
                        Database tuning & message queues
                      </Text>
                    </li>
                  </ul>
                </Card>

                <Card variant="glass" padding="md" className="mastery-category">
                  <Heading
                    as="h4"
                    size="lg"
                    variant="default"
                    className="mastery-title"
                  >
                    Cloud & Infrastructure
                  </Heading>
                  <ul className="mastery-list">
                    <li>
                      <Text size="xs" variant="secondary">
                        Cloud platform administration
                      </Text>
                    </li>
                    <li>
                      <Text size="xs" variant="secondary">
                        Containerization & orchestration
                      </Text>
                    </li>
                    <li>
                      <Text size="xs" variant="secondary">
                        Observability
                      </Text>
                    </li>
                    <li>
                      <Text size="xs" variant="secondary">
                        Fault tolerance & chaos engineering
                      </Text>
                    </li>
                  </ul>
                </Card>

                <Card variant="glass" padding="md" className="mastery-category">
                  <Heading
                    as="h4"
                    size="lg"
                    variant="default"
                    className="mastery-title"
                  >
                    AI Systems Engineering
                  </Heading>
                  <ul className="mastery-list">
                    <li>
                      <Text size="xs" variant="secondary">
                        LLM-powered DevOps workflows
                      </Text>
                    </li>
                    <li>
                      <Text size="xs" variant="secondary">
                        Prompt engineering & LLM integration
                      </Text>
                    </li>
                    <li>
                      <Text size="xs" variant="secondary">
                        MCP, A2A, RAG
                      </Text>
                    </li>
                    <li>
                      <Text size="xs" variant="secondary">
                        Automated testing & release pipelines
                      </Text>
                    </li>
                  </ul>
                </Card>

                <Card variant="glass" padding="md" className="mastery-category">
                  <Heading
                    as="h4"
                    size="lg"
                    variant="default"
                    className="mastery-title"
                  >
                    Leadership
                  </Heading>
                  <ul className="mastery-list">
                    <li>
                      <Text size="xs" variant="secondary">
                        Technical leadership & strategy
                      </Text>
                    </li>
                    <li>
                      <Text size="xs" variant="secondary">
                        Mentorship & team building
                      </Text>
                    </li>
                    <li>
                      <Text size="xs" variant="secondary">
                        Cross-functional problem solving
                      </Text>
                    </li>
                  </ul>
                </Card>
              </div>
            </section>

            {/* Key Achievements */}
            <section className="resume-section">
              <Heading
                as="h3"
                size="xl"
                variant="default"
                className="section-title"
              >
                Key Achievements
              </Heading>

              <Card variant="surface" padding="lg">
                <div className="achievements-grid">
                  <div className="achievement-item">
                    <Text
                      size="lg"
                      variant="accent"
                      className="achievement-metric"
                    >
                      22
                    </Text>
                    <Text size="xs" variant="tertiary">
                      Shipped games on 8 platforms
                    </Text>
                  </div>

                  <div className="achievement-item">
                    <Text
                      size="lg"
                      variant="accent"
                      className="achievement-metric"
                    >
                      14
                    </Text>
                    <Text size="xs" variant="tertiary">
                      Applications launched
                    </Text>
                  </div>

                  <div className="achievement-item">
                    <Text
                      size="lg"
                      variant="accent"
                      className="achievement-metric"
                    >
                      40%
                    </Text>
                    <Text size="xs" variant="tertiary">
                      Reduction in annual cloud costs
                    </Text>
                  </div>

                  <div className="achievement-item">
                    <Text
                      size="lg"
                      variant="accent"
                      className="achievement-metric"
                    >
                      100,000+
                    </Text>
                    <Text size="xs" variant="tertiary">
                      CCU burst scalability with zero overprovisioning
                    </Text>
                  </div>

                  <div className="achievement-item">
                    <Text
                      size="lg"
                      variant="accent"
                      className="achievement-metric"
                    >
                      4x
                    </Text>
                    <Text size="xs" variant="tertiary">
                      Developer throughput using LLM-powered DevOps automations
                    </Text>
                  </div>
                </div>
              </Card>
            </section>

            {/* Education */}
            <section className="resume-section">
              <Heading
                as="h3"
                size="xl"
                variant="default"
                className="section-title"
              >
                Education
              </Heading>

              <Card variant="surface" padding="lg" className="education-item">
                <div className="education-header">
                  <div className="education-degree">
                    <Heading as="h4" size="lg" variant="default">
                      Bachelor of Science in Computer Science
                    </Heading>
                    <Text size="sm" variant="secondary">
                      Minors in Physics and Mathematics
                    </Text>
                  </div>
                  <Text
                    size="base"
                    variant="accent"
                    className="education-school"
                  >
                    Missouri State University
                  </Text>
                </div>
              </Card>
            </section>
          </div>
        </Container>
      </main>
    </>
  )
}

export default ResumePage
